import React from 'react';
import bemify, { toModifiers } from 'bemify-js';
import { isEmpty } from 'lodash';

import { HideFooter } from '@zapier/common-components';

import Link from 'app/common/components/Link';
import { PARTNER_EMAIL_ADDRESS } from 'app/developer-v3/constants';
import { useActiveProfile } from 'app/developer-v3/hooks';

import GuideMenu from '../components/GuideMenu';
import GuideSidebar from '../components/GuideSidebar';
import GuideSidebarFooter from '../components/GuideSidebarFooter';
import StepContent from '../components/StepContent';
import {
  getGuideStepByKey,
  getGuideStepByPath,
  scrollToTopOfStepContent,
  updateGuideProgress,
} from '../utils';
import { useProgress } from '../hooks';
import { GUIDE_SECTIONS, GUIDE_STEPS } from '../constants';

import './App.scss';

const bem = bemify('cli-guide');

type Props = {
  stepPath: string;
};

const App = (props: Props) => {
  const { stepPath } = props;

  const [menuToggled, setMenuToggled] = React.useState(false);

  const { activeProfile } = useActiveProfile();
  const isLoggedIn = !!activeProfile;
  const { progress, mutate: updateProgress } = useProgress();

  const step = getGuideStepByPath(stepPath);
  const stepToRender =
    'redirectTo' in step ? getGuideStepByKey(step.redirectTo) : step;
  const progressPercentage = !isEmpty(progress.stepsSeen)
    ? (progress.stepsSeen.length / GUIDE_STEPS.length) * 100
    : 0;

  const onMenuToggle = () => setMenuToggled(!menuToggled);

  return (
    <HideFooter>
      <div className={bem()}>
        <div className={bem('__container')}>
          <div
            className={bem(
              '__sidebar',
              toModifiers({ 'menu-toggled': menuToggled })
            )}
            role="presentation"
          >
            <GuideMenu
              menuToggled={menuToggled}
              onMenuToggle={onMenuToggle}
              section={stepToRender.section}
              step={stepToRender.title}
            />
            <GuideSidebar
              currentStep={stepToRender.key}
              footer={
                <GuideSidebarFooter>
                  Email{' '}
                  <Link href={`mailto:${PARTNER_EMAIL_ADDRESS}`}>
                    {PARTNER_EMAIL_ADDRESS}
                  </Link>
                  .
                </GuideSidebarFooter>
              }
              menuToggled={menuToggled}
              onSelectStep={() => {
                scrollToTopOfStepContent();
                onMenuToggle();
              }}
              progress={progressPercentage}
              sections={[GUIDE_SECTIONS.SETUP, GUIDE_SECTIONS.TUTORIAL]}
              steps={GUIDE_STEPS}
              stepsSeen={progress.stepsSeen}
            />
          </div>
          <StepContent
            onClickBack={scrollToTopOfStepContent}
            onClickNext={() => {
              scrollToTopOfStepContent();
              if (isLoggedIn) {
                updateProgress(
                  updateGuideProgress(stepToRender, progress.stepsSeen)
                );
              }
            }}
            step={stepToRender}
          />
        </div>
      </div>
    </HideFooter>
  );
};

export default App;
